import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Iconify } from '@/components/iconify';
import { createFileRoute } from '@tanstack/react-router';
import { WhiteGloveLayout } from '@/sections/white-glove/white-glove-layout';
import { WgCalendarView } from '@/sections/white-glove/calendar/wg-calendar-view';
import { toViewMode, type CalendarViewMode } from '@/sections/calendar/calendar-shared';
import { CalendarUpgradeDialog } from '@/sections/white-glove/calendar/calendar-upgrade-dialog';
import { WgCalendarSettingsDrawer } from '@/sections/white-glove/calendar/calendar-settings-drawer';
import {
  hasWhiteGloveWork,
  useWhiteGloveAccess,
} from '@/sections/white-glove/use-white-glove-access';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import { DashboardContent } from 'src/layouts/dashboard';

/**
 * The calendar, which is a White Glove surface and nothing else.
 *
 * A calendar is a month somebody else planned: dates held against a production
 * lead time, approvals, gap filling, work scheduled to send. That is the service,
 * not a feature of the builder. A brand that has not bought the service still
 * sees its calendar, so it can see what the service would fill; reaching to plan
 * anything on it is where the offer to buy it stands.
 *
 * The brand's engagement decides, not the URL. `/white-glove/calendar` redirects
 * here.
 */
export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/calendar/')({
  component: CalendarPage,
  // The view mode is in the URL so a reload keeps it and a link carries it. It
  // is also how the redirect from the old month route lands on the folder.
  // `settings=open` is the same idea for the schedule drawer, so the tasks
  // page's Change button can land on the calendar with it already open.
  // `brief` is an open brief, so a brief can be linked to before it has a
  // design (and with it a review page) of its own.
  validateSearch: (
    search: Record<string, unknown>
  ): { view?: CalendarViewMode; settings?: 'open'; brief?: string } => ({
    view: toViewMode(search.view),
    settings: search.settings === 'open' ? 'open' : undefined,
    brief: typeof search.brief === 'string' && search.brief ? search.brief : undefined,
  }),
});

function CalendarPage() {
  const { t } = useTranslation('whiteGlove');
  const navigate = Route.useNavigate();
  const { view, settings, brief } = Route.useSearch();

  // Replace rather than push: flipping between a plan and a list is not a place
  // the back button should have to walk through.
  const onViewChange = (next: CalendarViewMode) =>
    navigate({ search: (prev) => ({ ...prev, view: next }), replace: true });

  // Replaced for the same reason: Back after closing the drawer should leave
  // the calendar, not reopen the settings.
  const setSettingsOpen = (open: boolean) =>
    navigate({
      search: (prev) => ({ ...prev, settings: open ? 'open' : undefined }),
      replace: true,
    });

  // Pushed, unlike the two above: opening a brief is a place, so Back closes
  // it. Closing replaces, so Back does not reopen it.
  const onOpenBriefChange = (next: string | null) =>
    navigate({
      search: (prev) => ({ ...prev, brief: next ?? undefined }),
      replace: !next,
    });

  // The relationship rather than the entry gate: every brand can open White
  // Glove, and only a brand with a live engagement has a month to plan.
  const { relationship, isTrial, isLoading } = useWhiteGloveAccess();
  const hasWork = hasWhiteGloveWork(relationship);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Wait rather than guess. Rendering the calendar locked and unlocking it once
  // the tier arrives would put the offer in front of a paying customer.
  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  // Every brand sees its calendar. A trial is two designs and no month, and a
  // brand with no engagement has no month either, so for both the calendar is
  // something to look at: every gesture that would plan something opens the
  // offer to buy the service instead (the view decides which gestures).
  const locked = !hasWork || isTrial;
  const openUpgrade = () => setUpgradeOpen(true);

  const settingsButton = (
    <Button
      variant="outlined"
      color="inherit"
      startIcon={<Iconify icon="solar:settings-linear" width={18} />}
      onClick={() => (locked ? openUpgrade() : setSettingsOpen(true))}
    >
      {t('calendar.settings.open', 'Settings')}
    </Button>
  );

  const body = (
    <>
      <WgCalendarView
        view={view ?? 'plan'}
        onViewChange={onViewChange}
        openBrief={brief ?? null}
        onOpenBriefChange={onOpenBriefChange}
        onLockedAction={locked ? openUpgrade : undefined}
        showMoments={hasWork}
      />
      {!locked && (
        <WgCalendarSettingsDrawer
          open={settings === 'open'}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      {/* Closed on its own once the purchase lands: the brand stops being
          locked and the offer has nothing left to say. */}
      <CalendarUpgradeDialog open={upgradeOpen && locked} onClose={() => setUpgradeOpen(false)} />
    </>
  );

  // The White Glove chrome answers a brand with no engagement with the free
  // designs page instead of its children, so that brand gets the calendar in a
  // plain page with the same title row.
  if (!hasWork) {
    return (
      <DashboardContent maxWidth="xl" sx={{ pt: { xs: 2, sm: 4 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: { xs: 2, sm: 3 }, gap: 1.5 }}
        >
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{ fontSize: { xs: 24, sm: 'inherit' }, lineHeight: 1.2, flex: 1, minWidth: 0 }}
          >
            {t('tabs.calendar', 'Calendar')}
          </Typography>
          {settingsButton}
        </Stack>
        {body}
      </DashboardContent>
    );
  }

  return (
    <WhiteGloveLayout title={t('tabs.calendar', 'Calendar')} actions={settingsButton}>
      {body}
    </WhiteGloveLayout>
  );
}
